import { useLoggedIn } from '@app/ACL/useLogin';
import {
	BusinessInfoAPI,
	useBusinessInfo,
} from '@app/BusinessAccount/useBusinessInfo';
import { useLoyaltySettings } from '@app/data/LoyaltyProgramme';
import { TLoyaltySettings } from '@modules/LoyaltyProgramme/types';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { IntercomPageContext } from './types';
import { DRAWER_ROOT_ID } from '@zeal/web-ui';
import { hideChatBubble, showChatBubble } from '@app/DialogFlow';

declare global {
	interface Window {
		Intercom: {
			booted: boolean;
		};
	}
}

/**
 * Component used to manage Intercom integration for Zeal merchants.
 *
 * Key responsibilities:
 * - Boots Intercom with user and company information for Zeal merchants
 * - Updates Intercom state based on page context and drawer visibility
 * - Handles Intercom shutdown for non-Zeal merchants
 *
 * Note: This component is specifically for Zeal merchants (not white label).
 * For functions to manage the intercom window, see useIntercomManager.ts
 *
 * @returns null
 */
export default function IntercomManager() {
	const { i18n } = useTranslation();
	const { merchantType } = useLoggedIn();
	const { data: businessInfo } = useBusinessInfo();
	const { data: loyaltySettings } = useLoyaltySettings();
	const { boot, shutdown, update, hide } = useIntercom();

	const { pathname } = useLocation();

	const bootIntercom = useCallback(
		(businessInfo: BusinessInfoAPI, loyaltySettings: TLoyaltySettings) => {
			// unix timestamp in seconds of when the account was created
			const createdAt = businessInfo?.created_at
				? new Date(businessInfo?.created_at).getTime() / 1000
				: undefined;
			const language = i18n.language.split('-')[0];

			boot({
				name: businessInfo?.name,
				email: businessInfo?.email,
				userId: businessInfo?.uuid,
				createdAt: createdAt,
				languageOverride: language,
				avatar: {
					imageUrl: businessInfo?.business?.photo,
					type: 'avatar',
				},
				company: {
					name: businessInfo?.business?.name,
					companyId: businessInfo?.business?.uuid,
					plan: businessInfo?.type,
					customAttributes: {
						loyalty_program: loyaltySettings?.loyalty_channel?.name,
						loyalty_strategy: loyaltySettings?.loyalty_strategy,
					},
				},
			});
		},
		[i18n.language, boot]
	);

	// merchantType is null for white label merchants
	const shouldBoot = useMemo(() => {
		return Boolean(merchantType);
	}, [merchantType]);

	/**
	 * Boots Intercom with merchant information when:
	 * - User is a Zeal merchant (not white label)
	 * - Business info and loyalty settings are loaded
	 *
	 * Shuts down Intercom when:
	 * - Component unmounts
	 * - User is not a Zeal merchant
	 * - Business info or loyalty settings are not available
	 */
	useEffect(() => {
		if (shouldBoot && businessInfo && loyaltySettings) {
			bootIntercom(businessInfo, loyaltySettings);
		} else if (window.Intercom.booted) {
			shutdown();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldBoot, businessInfo, loyaltySettings]);

	/**
	 * Updates Intercom's page context when route changes.
	 * This resets any page-specific context that was set when the widget
	 * was opened programmatically via useIntercomManager.ts.
	 *
	 * The page context helps track user navigation and provide
	 * relevant support context in Intercom conversations.
	 */
	useEffect(() => {
		if (window.Intercom.booted) {
			update({
				customAttributes: {
					page_context: IntercomPageContext.GeneralDashboard,
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname]);

	/**
	 * Manages Intercom launcher visibility based on drawer state.
	 *
	 * Implementation details:
	 * - Uses MutationObserver to detect drawer state changes
	 * - Hides Intercom launcher & widget when any drawer is open
	 * - Shows launcher when all drawers are closed
	 * - Watches for changes to data-drawer-open attribute
	 *
	 * Note: This ensures Intercom doesn't overlap with drawer content
	 * and provides a better user experience.
	 */
	useEffect(() => {
		const check = () => {
			const drawers = document.querySelectorAll(
				`[id^="${DRAWER_ROOT_ID}"] [data-testid="drawer"][data-drawer-open="true"]`
			);
			const isDrawerOpen = drawers.length > 0;

			if (window.Intercom.booted) {
				update({
					hideDefaultLauncher: isDrawerOpen,
				});
				if (isDrawerOpen) {
					hide();
					hideChatBubble();
				} else {
					showChatBubble();
				}
			}
		};

		// Run once in case a drawer is already open
		check();

		// Observe DOM changes
		const observer = new MutationObserver(check);
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['data-drawer-open'],
		});

		return () => observer.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
}
